import { useScrollValue } from "@components/AnimatedStore";
import ListHeader from "@components/Collection/ListHeader";
import LBox from "@components/LBox";
import Song from "@components/Song";
import BottomBarPadding from "@components/BottomBarPadding";
import type { Song as SongType } from "@libs/wire";
import { theme } from "@theme";
import { keyExtractor } from "@utils/data";
import ReanimatedFlashList from "@components/Account/Library/ReanimatedFlashList";
import {
  SharedValue,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import TopBarPadding from "@components/TopBarPadding";
import {
  Platform,
  Dimensions,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useIsFocused } from "expo-router/react-navigation";
import { useFocusEffect } from "expo-router";
import { useCallback, useMemo } from "react";
import PlaylistRecommendations from "@components/Collection/playlist/PlaylistRecommendations";

interface Props {
  blurhash?: string | null | (string | null)[];
  id: string;
  image?: string | null | (string | null | undefined)[];
  isFetching: boolean;
  isOwner: boolean;
  isPublic: boolean;
  onPress: () => void;
  onSelectSong: (id: string) => void;
  songs: SongType[];
  subtitle?: string;
  subtitleNumberOfLines?: number;
  title?: string;
  type: "album" | "playlist";
  hasPrivateSong?: boolean;
}

const { height } = Dimensions.get("window");

function updateScrollValue(scrollValue: SharedValue<number>, value: number) {
  scrollValue.value = value;
}

const List = ({
  blurhash,
  id,
  image,
  isFetching,
  isOwner,
  isPublic,
  onPress,
  onSelectSong,
  songs,
  subtitle,
  subtitleNumberOfLines,
  title,
  type,
  hasPrivateSong,
}: Props) => {
  const scrollValue = useScrollValue();
  const isFocused = useIsFocused();

  // Write the offset on the UI thread: hopping to JS on every scroll event
  // queues behind row mounts and makes the header animations stutter.
  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll(event) {
        if (isFocused) {
          scrollValue.set(event.contentOffset.y);
        }
      },
    },
    [isFocused],
  );

  const webScrollHandler = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (isFocused) {
        scrollValue.set(event.nativeEvent.contentOffset.y);
      }
    },
    [isFocused, scrollValue],
  );

  const handlePressSong = useCallback(
    (song: SongType) => {
      onSelectSong(song.id);
    },
    [onSelectSong],
  );

  const shouldDisplayImage = type === "playlist";

  // Stable renderItem so memo(Song) holds when List re-renders.
  const renderItem = useCallback(
    ({ item, index }: { item: SongType; index: number }) => (
      <LBox bg="screenBg" pt="s4" px="s4">
        <Song
          catalogSong={item}
          collectionId={id}
          collectionType={type}
          index={index}
          onPress={handlePressSong}
          showArtwork={shouldDisplayImage}
        />
      </LBox>
    ),
    [handlePressSong, id, shouldDisplayImage, type],
  );

  const existingSongIds = useMemo(() => songs.map((song) => song.id), [songs]);

  useFocusEffect(() => {
    updateScrollValue(scrollValue, 0);
    return () => {
      updateScrollValue(scrollValue, 0);
    };
  });

  return (
    <LBox height="100%">
      {/* Recycles row views: mounting fresh rows mid-scroll drops frames. */}
      <ReanimatedFlashList
        bounces={false}
        onScroll={Platform.OS === "web" ? webScrollHandler : scrollHandler}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        contentContainerStyle={{
          flexGrow: 1,
          height: Platform.select({
            web: height,
            default: undefined,
          }),
        }}
        data={songs}
        style={{ flexGrow: 1 }}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={
          <ListHeader
            blurhash={blurhash}
            id={id}
            image={image && image.length > 0 ? image : undefined}
            isFetching={isFetching}
            isOwner={isOwner}
            isPublic={isPublic}
            onPress={onPress}
            subtitle={subtitle}
            subtitleNumberOfLines={subtitleNumberOfLines}
            title={title}
            type={type}
            y={scrollValue}
            hasPrivateSong={hasPrivateSong}
          />
        }
        ListFooterComponent={
          <LBox bg="screenBg">
            {type === "playlist" && isOwner ? (
              <PlaylistRecommendations
                existingSongIds={existingSongIds}
                playlistId={id}
              />
            ) : null}
            <TopBarPadding />
            {Platform.OS !== "web" && <LBox height="100%" />}
            <BottomBarPadding defaultPadding={theme.spacing.m1} />
          </LBox>
        }
        // Needed for when collection is empty and bar music player is not shown
        ListEmptyComponent={<LBox flex={1} bg="screenBg" />}
      />
    </LBox>
  );
};

export default List;
